import { DateTime } from 'luxon'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import Rails from '@rails/ujs'
import React from 'react'

import Answer from './Answer'
import Feedback from './Feedback'
import Question from './Question'
import Timer from '../Global/Timer'

const QuestionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: stretch;
  width: 100%;
  height: 100%;
`

const QuestionHeader = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  text-align: right;
  margin-bottom: 2em;
`

const Answers = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(495px, 100%), 1fr));
  grid-gap: .5em;
  @media (min-width: 768px) {
    grid-gap: 2em;
  }
  max-width: 1500px;
  margin: 0px auto;
  width: 100%;
  margin-bottom: 2em;
  margin-top: 2em;
`

const QuestionContent = styled.div`
  flex-grow: 1;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: stretch;
`

const QuestionFooter = styled.div`
  display: inline-flex;
  justify-content: space-between;
  width: 100%;
  align-items: flex-end;
  min-height: 50px;
`

const QuizProgress = styled.div`
  text-align: left;
`

const SubmitButton = styled.div`
  text-align: right;
  margin-bottom: 1em;
`

const TimeoutWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
`

const Timeout = styled.div`
  font-size: 1.3em;
  @media (min-width: 768px) {
    font-size: 1.6em;
  }
  max-width: 900px;
  margin: 0px auto;
  text-align: center;
  margin-bottom: 1em;
`

const ShowQuestion = styled.div`
  float: right;
  cursor: pointer;

  svg {
    margin-right: 0.5rem;
  }
`

export default class QuestionAttemptForm extends React.Component {
  static propTypes = {
    questionAttempt: PropTypes.object.isRequired, // this is passed from the Rails view
    quiz: PropTypes.object.isRequired,
    totalQuestions: PropTypes.number
  }

  static defaultProps = {
    totalQuestions: 0
  }

  constructor(props) {
    super(props);

    let { questionAttempt } = props
    let answerOrder = ['a', 'b', 'c', 'd', 'e', 'f']
    if (questionAttempt.question.randomize_answers) {
      answerOrder.sort(() => { return Math.random() - 0.5 })
    }

    // How to set initial state in ES6 class syntax
    // https://reactjs.org/docs/state-and-lifecycle.html#adding-local-state-to-a-class
    this.state = {
      answerOrder: answerOrder,
      a_selected: false,
      b_selected: false,
      c_selected: false,
      d_selected: false,
      e_selected: false,
      f_selected: false,
      timedOut: false,
      redirect_path: null,
      feedback: {},
      showQuestionOverFeedback: false,
      correctAnswers: []
    }
  }

  selectOption = (name) => {
    let selected = this.state[name]
    let callback = this.submitAnswer
    let answers = ['a', 'b', 'c', 'd', 'e', 'f']
    let waiting = answers.length

    if (this.props.questionAttempt.question.require_submit_to_confirm) {
      callback = () => {}
    }

    /* Wait for all the question states to be set before potentially submitting */
    let callbackWait = () => {
      waiting--
      if (waiting == 0) {
        callback()
      }
    }

    if (this.props.questionAttempt.question.only_allow_single_answer) {
      answers.forEach((answer) => {
        let answerName = `${answer}_selected`
        if (answerName === name) {
          this.setState({
            [name]: !selected
          }, callbackWait)
        } else {
          this.setState({
            [answerName]: false
          }, callbackWait)
        }
      })
    } else {
      this.setState({
        [name]: !selected
      }, callback)
    }

  }

  submitAnswer = () => {
    fetch(`/quiz_attempt_questions/${this.props.questionAttempt.id}`, {
      method: 'PUT',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'X-CSRF-Token': Rails.csrfToken(),
        'Content-Type': 'application/json'
      },
      redirect: 'follow',
      credentials: 'same-origin',
      body: JSON.stringify({ quiz_attempt_question: {
        selected_answer_a: this.state.a_selected,
        selected_answer_b: this.state.b_selected,
        selected_answer_c: this.state.c_selected,
        selected_answer_d: this.state.d_selected,
        selected_answer_e: this.state.e_selected,
        selected_answer_f: this.state.f_selected
      } })
    })
      .then((data) => {
        return data.json()
      })
      .then((response) => {
        if (response.redirect_path) {
          if (this.props.quiz.hide_question_feedback) {
            window.location.replace(response.redirect_path)
          }
          let correctAnswers = response.correct_answers && Object.keys(response.correct_answers).length > 0
            ? response.correct_answers : null

          this.setState({
            redirect_path: response.redirect_path,
            feedback: response.feedback,
            correctAnswers: correctAnswers
          })
        }
      })
  }

  questionTimeOut = () => {
    this.setState({ timedOut: true })

    // Set timeout info on quiz attempt, reloading show will then
    // show results
    fetch(`/quiz_attempt_questions/${this.props.questionAttempt.id}/timeout`, {
      method: 'PUT',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'X-CSRF-Token': Rails.csrfToken(),
        'Content-Type': 'application/json'
      },
      redirect: 'follow',
      credentials: 'same-origin'
    })
  }

  toggleQuestion = () => {
    this.setState({ showQuestionOverFeedback: !this.state.showQuestionOverFeedback })
  }

  render() {
    let { questionAttempt, quiz, totalQuestions } = this.props
    let timeSinceStartSeconds = Math.floor((DateTime.local() - DateTime.fromJSDate(new Date(questionAttempt.start_time))) / 1000)

    if (this.state.timedOut) {
      return (
        <TimeoutWrapper>
          <Timeout>
            You ran out of time for that question.
          </Timeout>
          <div className="btn btn-primary"
              onClick={() => { window.location.reload(false) }}>
            Next <i className="fas fa-arrow-right"></i>
          </div>
        </TimeoutWrapper>
      )
    }

    if (this.state.feedback && Object.keys(this.state.feedback).length > 0) {
      return(
        <QuestionWrapper>
          <QuestionContent>
            {
              quiz.can_review_question
              ? (
                <div>
                  {
                    /*
                    Note cannot (x ? <i></i> : <i></i>) needs to be wrapped in div
                    and be separate statements to trigger clean re-render
                  */
                  }
                  {
                    this.state.showQuestionOverFeedback
                      ? (
                        <ShowQuestion onClick={() => { this.toggleQuestion() }}>
                          <i className="far fa-check-square"></i>Review Question
                        </ShowQuestion>
                      )
                      : null
                  }
                  {
                    this.state.showQuestionOverFeedback
                      ? null
                      : (
                        <ShowQuestion onClick={() => { this.toggleQuestion() }}>
                          <i className="far fa-square"></i>Review Question
                        </ShowQuestion>
                      )
                  }
                </div>
              )
              : null
            }

            {
              this.state.showQuestionOverFeedback
                ? (
                  <Question question={questionAttempt.question} />
                )
                : (
                  <Feedback feedback={this.state.feedback}
                      hideQuestionFeedback={quiz.hide_question_feedback}>
                  </Feedback>
                )
            }

            <Answers>
                {
                  this.state.answerOrder.map((answer) => {
                    return questionAttempt.question[`answer_${answer}`]
                      ? <Answer answer={questionAttempt.question[`answer_${answer}`] }
                                selected={this.state[`${answer}_selected`]}
                                answered={true}
                                correct={this.state.correctAnswers && Object.keys(this.state.correctAnswers).length > 0
                                          ? this.state.correctAnswers.includes(answer)
                                          : null}
                                onClick={() => {}}
                                colour={answer}
                                key={`answer_${answer}`}
                                hideQuestionFeedback={quiz.hide_question_feedback}
                                quizVisualStyle={quiz.quiz_visual_style} />
                      : null
                  })
                }
              </Answers>
          </QuestionContent>
          <QuestionFooter>
            <QuizProgress>
              {
                quiz.show_progress_in_footer
                  ? `${questionAttempt.sequence}${totalQuestions ? '/' + totalQuestions : '' } `
                  : null
              }
            </QuizProgress>

            <SubmitButton className='btn btn-primary'
                onClick={() => {
                  window.location.replace(this.state.redirect_path)
                }}>
              Next <i className="fas fa-arrow-right"></i>
            </SubmitButton>
          </QuestionFooter>
        </QuestionWrapper>
      )
    }

    return (
      <QuestionWrapper>
        <QuestionHeader>
          <Timer startTimeSeconds={ timeSinceStartSeconds }
                 timeLimit={questionAttempt.question.question_time_limit}
                 overLimitCallback={this.questionTimeOut}
                 label={'Question'} />
        </QuestionHeader>
        <QuestionContent>
          <Question question={questionAttempt.question}/>

          <Answers>
            {
              this.state.answerOrder.map((answer) => {
                return questionAttempt.question[`answer_${answer}`]
                  ? <Answer answer={questionAttempt.question[`answer_${answer}`] }
                            selected={this.state[`${answer}_selected`]}
                            answered={false}
                            colour={answer}
                            onClick={() => { this.selectOption(`${answer}_selected`) }}
                            key={`answer_${answer}`}
                            hideQuestionFeedback={quiz.hide_question_feedback}
                            quizVisualStyle={quiz.quiz_visual_style} />
                  : null
              })
            }
          </Answers>
        </QuestionContent>
        <QuestionFooter>
          <QuizProgress>
            {
              quiz.show_progress_in_footer
                ? `${questionAttempt.sequence}${totalQuestions ? '/' + totalQuestions : '' } `
                : null
            }
          </QuizProgress>
          <SubmitButton>
            {
              questionAttempt.question.require_submit_to_confirm
                ? (
                  <div className='btn btn-primary'
                       onClick={this.submitAnswer}>
                    Next <i className="fas fa-arrow-right"></i>
                  </div>
                )
                : null
            }

          </SubmitButton>
        </QuestionFooter>
      </QuestionWrapper>
    )
  }
}
